/**
 * CAO Schoonmaak 2024-2026 — Loontabellen
 *
 * Bijlage III: Reguliere loontabellen (vast contract)
 *   - 6 loongroepen (1-6), 4 treden (dienstjaren 1-4)
 *   - Jeugdlonen (≤17, 18, 19) op basis van leeftijd
 *   - Dienstjaren tellen vanaf leeftijd 20
 *
 * Bijlage IIIA: All-in-loontabellen (0-uren, studenten, vakantiekrachten)
 *   - Inclusief vakantieopbouw (10%) + vakantietoeslag (8%)
 *   - Alleen op leeftijd, geen treden/dienstjaren
 */

// ── Salary Tables ──────────────────────────────────────────────────

interface SalaryTable {
  effectiveDate: string; // YYYY-MM-DD
  youth: {
    17: number[];  // ≤17 jaar, per loongroep 1-6
    18: number[];  // 18 jaar
    19: number[];  // 19 jaar
  };
  treden: {
    1: number[];  // dienstjaar 1, per loongroep 1-6
    2: number[];
    3: number[];
    4: number[];
  };
}

interface AllInSalaryTable {
  effectiveDate: string;
  ages: {
    17: number[];  // ≤17 jaar, per loongroep 1-6
    18: number[];
    19: number[];
    20: number[];  // 20 jaar en ouder
  };
}

const SALARY_TABLES: SalaryTable[] = [
  {
    effectiveDate: '2024-07-01',
    youth: {
      17: [10.94, 11.49, 12.02, 12.63, 13.12, 13.78],
      18: [11.62, 12.21, 12.77, 13.42, 13.93, 14.64],
      19: [12.30, 12.92, 13.51, 14.20, 14.75, 15.49],
    },
    treden: {
      1: [14.15, 14.87, 15.55, 16.34, 16.97, 17.83],
      2: [14.68, 15.39, 16.12, 16.91, 17.55, 18.45],
      3: [15.12, 15.90, 16.63, 17.46, 18.14, 19.05],
      4: [15.60, 16.40, 17.19, 18.01, 18.71, 19.67],
    },
  },
  {
    effectiveDate: '2025-01-01',
    youth: {
      17: [11.27, 11.84, 12.38, 13.01, 13.51, 14.19],
      18: [11.97, 12.58, 13.14, 13.82, 14.35, 15.07],
      19: [12.67, 13.31, 13.91, 14.63, 15.19, 15.95],
    },
    treden: {
      1: [14.58, 15.32, 16.01, 16.83, 17.48, 18.36],
      2: [15.12, 15.85, 16.60, 17.41, 18.08, 19.00],
      3: [15.58, 16.38, 17.13, 17.99, 18.69, 19.63],
      4: [16.06, 16.89, 17.70, 18.55, 19.28, 20.26],
    },
  },
  {
    effectiveDate: '2025-07-01',
    youth: {
      17: [11.46, 11.96, 12.45, 13.09, 13.59, 14.27],
      18: [12.18, 12.70, 13.23, 13.90, 14.43, 15.16],
      19: [12.89, 13.44, 14.00, 14.71, 15.28, 16.04],
    },
    treden: {
      1: [14.83, 15.47, 16.11, 16.93, 17.58, 18.46],
      2: [15.37, 16.00, 16.70, 17.51, 18.18, 19.10],
      3: [15.83, 16.53, 17.23, 18.09, 18.79, 19.73],
      4: [16.31, 17.04, 17.80, 18.65, 19.38, 20.36],
    },
  },
  {
    effectiveDate: '2026-01-01',
    youth: {
      17: [12.00, 12.43, 12.91, 13.55, 14.07, 14.78],
      18: [12.74, 13.20, 13.71, 14.39, 14.94, 15.70],
      19: [13.49, 13.97, 14.51, 15.23, 15.82, 16.62],
    },
    treden: {
      1: [15.52, 16.08, 16.70, 17.53, 18.20, 19.12],
      2: [16.08, 16.63, 17.30, 18.14, 18.82, 19.77],
      3: [16.55, 17.17, 17.84, 18.73, 19.45, 20.42],
      4: [17.05, 17.70, 18.44, 19.31, 20.06, 21.07],
    },
  },
];

// ── All-in Loontabellen (Bijlage IIIA) ─────────────────────────────
// Voor 0-uren, studenten, vakantiekrachten
// Inclusief vakantieopbouw (10%) + vakantietoeslag (8%)

const ALL_IN_TABLES: AllInSalaryTable[] = [
  {
    effectiveDate: '2024-07-01',
    ages: {
      17: [13.12, 13.79, 14.42, 15.16, 15.74, 16.54],
      18: [13.94, 14.65, 15.32, 16.10, 16.72, 17.56],
      19: [14.74, 15.50, 16.21, 17.04, 17.69, 18.59],
      20: [16.98, 17.84, 18.66, 19.61, 20.36, 21.39],
    },
  },
  {
    effectiveDate: '2025-01-01',
    ages: {
      17: [13.52, 14.21, 14.85, 15.61, 16.21, 17.03],
      18: [14.36, 15.09, 15.77, 16.58, 17.22, 18.09],
      19: [15.20, 15.97, 16.69, 17.55, 18.23, 19.14],
      20: [17.49, 18.38, 19.21, 20.19, 20.97, 22.03],
    },
  },
  {
    effectiveDate: '2025-07-01',
    ages: {
      17: [13.75, 14.35, 14.94, 15.70, 16.31, 17.12],
      18: [14.61, 15.24, 15.87, 16.68, 17.32, 18.18],
      19: [15.46, 16.13, 16.80, 17.65, 18.33, 19.25],
      20: [17.79, 18.56, 19.33, 20.31, 21.09, 22.15],
    },
  },
  {
    effectiveDate: '2026-01-01',
    ages: {
      17: [14.39, 14.91, 15.49, 16.26, 16.88, 17.73],
      18: [15.29, 15.84, 16.45, 17.27, 17.93, 18.84],
      19: [16.18, 16.77, 17.41, 18.28, 18.98, 19.94],
      20: [18.62, 19.29, 20.04, 21.03, 21.84, 22.94],
    },
  },
];

// ── Lookup Functions ───────────────────────────────────────────────

/**
 * Get the applicable salary table for a given date
 */
function getApplicableTable(date: string): SalaryTable {
  let table: SalaryTable = SALARY_TABLES[0]!;
  for (const t of SALARY_TABLES) {
    if (t.effectiveDate <= date) table = t;
  }
  return table;
}

function getApplicableAllInTable(date: string): AllInSalaryTable {
  let table: AllInSalaryTable = ALL_IN_TABLES[0]!;
  for (const t of ALL_IN_TABLES) {
    if (t.effectiveDate <= date) table = t;
  }
  return table;
}

/**
 * Calculate age at a given date
 */
function ageAt(geboortedatum: string, referenceDate: string): number {
  const birth = new Date(geboortedatum + 'T00:00:00');
  const ref = new Date(referenceDate + 'T00:00:00');
  let age = ref.getFullYear() - birth.getFullYear();
  const m = ref.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * Calculate dienstjaren at a given date
 * Dienstjaren only count from age 20 (Art. 16 lid 5)
 */
function dienstjarenAt(datumInDienst: string, geboortedatum: string | null, referenceDate: string): number {
  const start = new Date(datumInDienst + 'T00:00:00');
  const ref = new Date(referenceDate + 'T00:00:00');

  // If born, dienstjaren only count from age 20
  let effectiveStart = start;
  if (geboortedatum) {
    const turns20 = new Date(geboortedatum + 'T00:00:00');
    turns20.setFullYear(turns20.getFullYear() + 20);
    if (turns20 > start) effectiveStart = turns20;
  }

  if (ref < effectiveStart) return 0;

  let years = ref.getFullYear() - effectiveStart.getFullYear();
  const m = ref.getMonth() - effectiveStart.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < effectiveStart.getDate())) years--;
  return Math.max(0, years);
}

/**
 * Get the trede (1-4) based on dienstjaren
 * Art. 16: new = trede 1, grows after 1 year each
 */
function getTrede(dienstjaren: number): 1 | 2 | 3 | 4 {
  if (dienstjaren >= 3) return 4;
  if (dienstjaren >= 2) return 3;
  if (dienstjaren >= 1) return 2;
  return 1;
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Effectieve dienstjaren voor de CAO-trede: berekende dienstjaren uit
 * datum_in_dienst (tellend vanaf leeftijd 20, Art. 16 lid 5) plus erkende
 * ervaringsjaren elders (staff.cao_ervaringsjaren). De ervaring telt dóór:
 * volgend jaar is het resultaat automatisch één hoger (auto-advance),
 * in tegenstelling tot een statische override.
 *
 * Retourneert null als er geen ervaringsjaren zijn: de caller kan dan
 * gewoon zonder override rekenen (zelfde uitkomst, expliciet pad).
 */
export function effectieveDienstjaren(
  datumInDienst: string | null,
  geboortedatum: string | null,
  ervaringsjaren: number | null | undefined,
  referenceDate?: string,
): number | null {
  if (ervaringsjaren == null || ervaringsjaren < 0) return null;
  const refDate = referenceDate || new Date().toISOString().split('T')[0]!;
  const berekend = datumInDienst ? dienstjarenAt(datumInDienst, geboortedatum, refDate) : 0;
  return berekend + Math.floor(ervaringsjaren);
}

export interface CaoSalaryResult {
  uurloon: number;
  loongroep: number;
  trede: number | null;         // null for youth/all-in wages
  dienstjaren: number;
  leeftijd: number;
  isYouth: boolean;
  isAllIn: boolean;             // all-in-loon (0-uren, student)
  tableDate: string;            // effective date of the table used
}

/**
 * Calculate CAO basisuurloon for a staff member at a given date
 *
 * @param loongroep 1-6
 * @param datumInDienst YYYY-MM-DD
 * @param geboortedatum YYYY-MM-DD (null = assume adult)
 * @param loonType 'bruto' | 'all-in' (determines which CAO table to use)
 * @param referenceDate YYYY-MM-DD (default: today)
 * @param dienstjarenOverride handmatig toegekende dienstjaren ervaring (bv. erkende
 *   ervaring elders); vervangt de berekening o.b.v. datumInDienst voor de trede
 */
export function getCaoUurloon(
  loongroep: number,
  datumInDienst: string,
  geboortedatum: string | null,
  loonType?: 'bruto' | 'all-in',
  referenceDate?: string,
  dienstjarenOverride?: number | null
): CaoSalaryResult {
  const refDate = referenceDate || new Date().toISOString().split('T')[0]!;
  const lgIndex = Math.min(Math.max(loongroep, 1), 6) - 1; // 0-based

  // Without geboortedatum we cannot determine the correct wage bracket.
  // Return 0 to signal that this needs to be resolved first.
  if (!geboortedatum) {
    return {
      uurloon: 0, loongroep, trede: null, dienstjaren: 0,
      leeftijd: -1, isYouth: false, isAllIn: loonType === 'all-in',
      tableDate: '',
    };
  }

  const leeftijd = ageAt(geboortedatum, refDate);

  // Safe array access helper
  const loonFor = (arr: number[]): number => arr[lgIndex] ?? arr[0] ?? 0;

  // All-in-loon (Bijlage IIIA) — includes vakantieopbouw + vakantietoeslag
  const isAllIn = loonType === 'all-in';
  if (isAllIn) {
    const allInTable = getApplicableAllInTable(refDate);
    const ageKey: 17 | 18 | 19 | 20 = leeftijd <= 17 ? 17 : leeftijd === 18 ? 18 : leeftijd === 19 ? 19 : 20;
    return {
      uurloon: loonFor(allInTable.ages[ageKey]),
      loongroep, trede: null,
      dienstjaren: 0, leeftijd,
      isYouth: leeftijd < 20, isAllIn: true,
      tableDate: allInTable.effectiveDate,
    };
  }

  // Reguliere loontabel (Bijlage III)
  const table = getApplicableTable(refDate);

  // Youth wages (under 20)
  if (leeftijd <= 17) {
    return {
      uurloon: loonFor(table.youth[17]),
      loongroep, trede: null, dienstjaren: 0,
      leeftijd, isYouth: true, isAllIn: false, tableDate: table.effectiveDate,
    };
  }
  if (leeftijd === 18) {
    return {
      uurloon: loonFor(table.youth[18]),
      loongroep, trede: null, dienstjaren: 0,
      leeftijd, isYouth: true, isAllIn: false, tableDate: table.effectiveDate,
    };
  }
  if (leeftijd === 19) {
    return {
      uurloon: loonFor(table.youth[19]),
      loongroep, trede: null, dienstjaren: 0,
      leeftijd, isYouth: true, isAllIn: false, tableDate: table.effectiveDate,
    };
  }

  // Adult wages (20+): based on dienstjaren (handmatige ervaring wint van datum in dienst)
  const dj = dienstjarenOverride != null && dienstjarenOverride >= 0
    ? Math.floor(dienstjarenOverride)
    : dienstjarenAt(datumInDienst, geboortedatum, refDate);
  const trede = getTrede(dj);

  return {
    uurloon: loonFor(table.treden[trede]),
    loongroep, trede, dienstjaren: dj,
    leeftijd, isYouth: false, isAllIn: false, tableDate: table.effectiveDate,
  };
}

/**
 * Get the full salary table for display/reference
 */
export function getCurrentSalaryTable(referenceDate?: string): SalaryTable {
  const refDate = referenceDate || new Date().toISOString().split('T')[0]!;
  return getApplicableTable(refDate);
}
